/**
 * NativeBridge - Bidirectional communication bridge between web and native iOS
 * Provides Promise-based API with event subscriptions
 */

import { EventEmitter } from "./EventEmitter"
import type {
  NativeRequest,
  NativeResponse,
  SubscriptionRequest,
  UnsubscribeRequest,
  NativeMessage,
  NativeError,
} from "./types"
import { ErrorCode, NativeBridgeError, isNativeResponse, isNativeEvent, isSubscriptionResponse } from "./types"
import { addLogDebug, addLogError } from "../logger"
import { withRetry } from "./utils"
import type { RetryOptions } from "./utils"
import { mockNativeBridge } from "./mock"
import config from "@/config"

declare global {
  interface Window {
    isNativeBridgeReady?: boolean
  }
}

interface PendingRequest {
  resolve: (value: NativeResponse) => void
  reject: (reason: Error) => void
  timeout: NodeJS.Timeout
}

interface ActiveSubscription {
  event: string
  callback: (data: unknown) => void
}

export class NativeBridge extends EventEmitter {
  private static instance: NativeBridge | null = null
  private pendingRequests = new Map<string, PendingRequest>()
  private activeSubscriptions = new Map<string, ActiveSubscription>()
  private messageHandler: ((message: unknown) => void) | null = null
  private isInitialized = false
  private defaultTimeout = 30000 // 30 seconds
  private useMock = false

  private constructor() {
    super()
    this.initialize()
  }

  /**
   * Get singleton instance of NativeBridge
   */
  static getInstance(): NativeBridge {
    if (!NativeBridge.instance) {
      NativeBridge.instance = new NativeBridge()
    }
    return NativeBridge.instance
  }

  /**
   * Check if native bridge is available
   */
  static isAvailable(): boolean {
    return typeof window !== "undefined" && window.webkit?.messageHandlers.nativeBridge !== undefined
  }

  /**
   * Initialize the bridge
   */
  private initialize() {
    if (this.isInitialized) return

    // Check if we should use mock - only if explicitly requested
    this.useMock = config.USE_MOCK_NATIVE_BRIDGE || window.location.search.includes("mock=true")

    if (this.useMock) {
      addLogDebug("NativeBridge", "Using mock implementation (explicitly enabled)")
    } else if (!NativeBridge.isAvailable()) {
      addLogDebug("NativeBridge", "Native bridge not available - calls will fail")
    }

    // Set up message handler
    this.messageHandler = (event: unknown) => {
      const customEvent = event as CustomEvent<NativeMessage>
      this.handleNativeMessage(customEvent.detail)
    }

    // Listen for native messages
    window.addEventListener("native-message", this.messageHandler as EventListener)

    // Mark window as having native bridge
    window.isNativeBridgeReady = true

    this.isInitialized = true
    addLogDebug("NativeBridge initialized")
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.messageHandler) {
      window.removeEventListener("native-message", this.messageHandler as EventListener)
    }

    // Clear all pending requests
    this.pendingRequests.forEach(({ reject, timeout }) => {
      clearTimeout(timeout)
      reject(new Error("NativeBridge destroyed"))
    })
    this.pendingRequests.clear()

    // Clear subscriptions
    this.activeSubscriptions.clear()
    this.removeAllListeners()

    this.isInitialized = false
    NativeBridge.instance = null
  }

  /**
   * Send a request to native and wait for response
   */
  async request<TParams = unknown, TResponse = unknown>(
    method: string,
    params?: TParams,
    timeout?: number,
    retryOptions?: RetryOptions,
  ): Promise<TResponse> {
    // Use mock if enabled
    if (this.useMock) {
      return mockNativeBridge.request<TParams, TResponse>(method, params, timeout)
    }

    if (!NativeBridge.isAvailable()) {
      throw new Error("Native bridge not available")
    }

    // Wrap the request in retry logic if options provided
    const makeRequest = async () => {
      const requestId = this.generateId()
      const request: NativeRequest = {
        id: requestId,
        type: "request",
        method,
        params,
        timeout,
        timestamp: Date.now(),
      }

      return new Promise<TResponse>((resolve, reject) => {
        // Set up timeout
        const timeoutMs = timeout || this.defaultTimeout
        const timeoutHandle = setTimeout(() => {
          this.pendingRequests.delete(requestId)
          reject(this.createError(ErrorCode.TIMEOUT, `Request timed out after ${timeoutMs}ms`))
        }, timeoutMs)

        // Store pending request
        this.pendingRequests.set(requestId, {
          resolve: (response: NativeResponse) => {
            clearTimeout(timeoutHandle)
            this.pendingRequests.delete(requestId)

            // Handle iOS response format (uses result/error instead of status/data)
            if (response.error) {
              // NativeError type guarantees code and message properties
              reject(this.toError(response.error))
            } else if (response.result !== undefined) {
              resolve(response.result as TResponse)
            } else if (response.status === "success") {
              resolve(response.data as TResponse)
            } else {
              reject(this.createError(ErrorCode.INTERNAL_ERROR, "Unknown error"))
            }
          },
          reject: (error: Error) => {
            clearTimeout(timeoutHandle)
            this.pendingRequests.delete(requestId)
            reject(error)
          },
          timeout: timeoutHandle,
        })

        // Send request to native
        this.sendToNative(request)
        addLogDebug(`Request sent: ${method}`, { requestId, params })
      })
    }

    // Apply retry logic if options provided
    if (retryOptions) {
      return withRetry(makeRequest, retryOptions)
    }

    return makeRequest()
  }

  /**
   * Subscribe to native events
   */
  async subscribe<TData = unknown>(event: string, callback: (data: TData) => void, params?: unknown): Promise<string> {
    // Use mock if enabled
    if (this.useMock) {
      return mockNativeBridge.subscribe<TData>(event, callback)
    }

    const subscriptionId = this.generateId()

    const typedCallback = callback as (data: unknown) => void

    // Store subscription info
    this.activeSubscriptions.set(subscriptionId, {
      event,
      callback: typedCallback,
    })

    // Register event listener
    this.on(event, typedCallback)

    // Send subscription request to native and wait for response
    const request: SubscriptionRequest = {
      id: subscriptionId,
      type: "subscribe",
      event,
      params,
      timestamp: Date.now(),
    }

    // Create a promise to track the subscription response
    const responsePromise = new Promise<void>((resolve, reject) => {
      const timeoutMs = 5000 // 5 second timeout for subscriptions
      const timeoutHandle = setTimeout(() => {
        this.pendingRequests.delete(subscriptionId)
        reject(this.createError(ErrorCode.TIMEOUT, `Subscription timeout after ${timeoutMs}ms`))
      }, timeoutMs)

      // Store pending request
      this.pendingRequests.set(subscriptionId, {
        resolve: (response: NativeResponse) => {
          clearTimeout(timeoutHandle)
          this.pendingRequests.delete(subscriptionId)

          // Handle response
          if (response.error) {
            reject(this.toError(response.error))
          } else {
            resolve()
          }
        },
        reject: (error: Error) => {
          clearTimeout(timeoutHandle)
          this.pendingRequests.delete(subscriptionId)
          reject(error)
        },
        timeout: timeoutHandle,
      })
    })

    // Send the request
    this.sendToNative(request)

    try {
      await responsePromise
      addLogDebug(`Subscribed to event: ${event}`, { subscriptionId })
    } catch (error) {
      // Clean up on error
      this.activeSubscriptions.delete(subscriptionId)
      this.off(event, typedCallback)
      throw error
    }

    return subscriptionId
  }

  /**
   * Unsubscribe from native events
   */
  async unsubscribe(subscriptionId: string): Promise<void> {
    // Use mock if enabled
    if (this.useMock) {
      return mockNativeBridge.unsubscribe(subscriptionId)
    }

    const subscription = this.activeSubscriptions.get(subscriptionId)
    if (!subscription) {
      addLogError(`Subscription not found: ${subscriptionId}`)
      return
    }

    // Remove event listener
    this.off(subscription.event, subscription.callback)
    this.activeSubscriptions.delete(subscriptionId)

    // Send unsubscribe request to native and wait for response
    const request: UnsubscribeRequest = {
      id: subscriptionId,
      type: "unsubscribe",
      timestamp: Date.now(),
    }

    // Create a promise to track the unsubscribe response
    const responsePromise = new Promise<void>((resolve, _reject) => {
      const timeoutMs = 5000 // 5 second timeout for unsubscriptions
      const timeoutHandle = setTimeout(() => {
        this.pendingRequests.delete(subscriptionId)
        // Don't reject on timeout for unsubscribe, just resolve
        resolve()
      }, timeoutMs)

      // Store pending request
      this.pendingRequests.set(subscriptionId, {
        resolve: (_response: NativeResponse) => {
          clearTimeout(timeoutHandle)
          this.pendingRequests.delete(subscriptionId)
          resolve()
        },
        reject: (error: unknown) => {
          clearTimeout(timeoutHandle)
          this.pendingRequests.delete(subscriptionId)
          // Don't throw on unsubscribe errors, just log them
          addLogError("Unsubscribe error", error)
          resolve()
        },
        timeout: timeoutHandle,
      })
    })

    // Send the request
    this.sendToNative(request)

    try {
      await responsePromise
      addLogDebug(`Unsubscribed from event: ${subscription.event}`, { subscriptionId })
    } catch (error) {
      // Log but don't throw for unsubscribe errors
      addLogError("Failed to unsubscribe", error)
    }
  }

  /**
   * Handle messages from native
   */
  private handleNativeMessage(message: NativeMessage) {
    addLogDebug("Received native message", message)

    if (isNativeResponse(message)) {
      // Handle response to a request
      const pending = this.pendingRequests.get(message.id)
      if (pending) {
        pending.resolve(message)
      } else {
        addLogError("No pending request for response", { id: message.id })
      }
    } else if (isNativeEvent(message)) {
      // Handle event from native
      this.emit(message.event, message.data)
    } else if (isSubscriptionResponse(message)) {
      // Handle subscription response (currently not used by native implementation)
      if (message.status === "error" && message.error) {
        addLogError("Subscription error", message.error)
      }
    }
  }

  /**
   * Send message to native
   */
  private sendToNative(message: NativeMessage) {
    if (!window.webkit?.messageHandlers.nativeBridge) {
      throw new Error("Native bridge not available")
    }

    window.webkit.messageHandlers.nativeBridge.postMessage(message)
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
  }

  /**
   * Create error object
   */
  private createError(code: string, message: string, details?: unknown): NativeBridgeError {
    return new NativeBridgeError(code, message, details)
  }

  /** The native side answers with a plain `{ code, message }`; rejections are always Errors. */
  private toError(error: NativeError): NativeBridgeError {
    return new NativeBridgeError(error.code, error.message, error.details)
  }
}

// Export singleton instance getter
export const nativeBridge = NativeBridge.getInstance()

// Note: Platform utilities should be imported directly from "./platform"
// to avoid circular dependency with NativeBridge class
